import { useEffect, useRef, useState } from 'react';

import type { FunctionComponent } from 'react';
import type { TypeWhatsappPopupProps } from './types';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/**
 * WhatsappPopup
 */
export const WhatsappPopup: FunctionComponent<TypeWhatsappPopupProps> = ({
  label,
  url,
  icon,
}: TypeWhatsappPopupProps) => {
  const dockRef = useRef<HTMLDivElement | null>(null);
  const mqRef = useRef<HTMLDivElement | null>(null);

  const [isMobile, setIsMobile] = useState(false);
  const [dockHeight, setDockHeight] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateIsMobile = () => {
      const el = mqRef.current;
      if (!el) return;
      const display = window.getComputedStyle(el).display;
      setIsMobile(display !== 'none');
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    window.addEventListener('orientationchange', updateIsMobile);

    return () => {
      window.removeEventListener('resize', updateIsMobile);
      window.removeEventListener('orientationchange', updateIsMobile);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || !dockRef.current) return;

    const updateHeight = () => {
      const h = dockRef.current?.getBoundingClientRect().height || 0;
      setDockHeight(h);
    };

    updateHeight();

    const ro = new ResizeObserver(updateHeight);
    ro.observe(dockRef.current);

    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
      ro.disconnect();
    };
  }, [isMobile]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (isMobile) {
      document.body.style.paddingBottom = `${dockHeight}px`;
    } else {
      document.body.style.paddingBottom = '';
    }
  }, [isMobile, dockHeight]);

  const href = (url ?? '').trim();
  if (!href) return null;

  const text = (label ?? '').trim();

  const isExternal = /^https?:\/\//i.test(href);

  const pushWhatsappLead = () => {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'contacto',
      content_type: 'cta',
      content_id: 'whatsapp_sticky',
    });
  };

  return (
    <>
      <div
        ref={mqRef}
        aria-hidden
        className="pointer-events-none absolute -z-50 block h-0 w-0 overflow-hidden md:hidden"
      />

      <div
        ref={dockRef}
        className={`
        fixed inset-x-0 bottom-0
          z-50 
          bg-[#25D366] md:inset-auto md:bottom-6
          md:right-6 md:rounded-xl
          md:shadow-xl
        `}
      >
        <div className="flex flex-col items-center justify-center gap-1 md:flex-row md:items-start md:gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-center">
            <div className="flex items-center justify-center p-5">
              {icon ? (
                <img
                  className="me-3 h-6 w-6"
                  src={icon}
                  alt={text || 'Icon'}
                  loading="lazy"
                />
              ) : null}

              <a
                href={href}
                onClick={pushWhatsappLead}
                className="text-[18px] font-bold text-white underline"
                {...(isExternal
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
              >
                {label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

WhatsappPopup.displayName = 'WhatsappPopup';

export default WhatsappPopup;
